import { Component, NgZone, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { HrService } from '../hr.service';
import { CommonService } from '@app/common/common.service';
import { LoginService } from '@app/common/login.service';
import { AlertService } from '@app/shared/alert.service';
import {
	HrActivity, HrAbsence, HrOverride, HrRequirement, HrSchedule,
	hrIsHrManager, hrOpeningOf, hrRequirementActivityId, hrResolveActivities,
} from '@app/shared/_models/hr';
import { Store } from '@app/shared/_models/common';
import {
	EVENTTYP_DELETE, EVENTTYP_INSERT, EVENTTYP_UPDATE, HR_ABSENCE_STATE_APPROVED,
	HR_COVERAGE_WEEKS, HR_GRID_SNAP_MIN, OBJECTTYP_HR_REQUIREMENT,
} from '@app/shared/constants';
import { CoverageShortfall, CoverageSurplus, addDays, computeCoverage, computeOverstaffing, isoWeekOf, minutesLabel, minutesOfHhmm, mondayOf } from '../hr-schedule-engine';
import { HR_DAY_LABELS } from '../schedule/schedule.component';
import { todayStr } from '@app/shared/time';

// Une tranche éditée : « de 09:00 à 12:00 il faut 3 personnes ».
export interface SliceRow {
	start: string;			// « HH:MM »
	end: string;
	min: number | null;
}

// Une « gestion » : des jours regroupés qui partagent les mêmes tranches (« Lun–Ven », « Sam »…).
export interface CoverageGroup {
	days: number[];			// 1..7, triés
	rows: SliceRow[];
}

// Les deux écarts partagent EXACTEMENT la même mise en situation (semaine, jour, établissement,
// service, tranche) : seule diffère la conclusion — il manque du monde, ou il y en a de trop.
interface GapVM {
	week: number;
	dayLabel: string;
	storeName: string;
	activityName: string;
	hours: string;
}

interface ShortfallVM extends CoverageShortfall, GapVM {}
interface SurplusVM extends CoverageSurplus, GapVM {}

// Un élément de l'histogramme d'une gestion : ouverture du service (fond) ou tranche
// d'effectif (bloc dont la hauteur ET la couleur suivent le nombre de personnes).
export interface TimelineSeg {
	left: number;			// %
	width: number;		// %
	cls: 'open' | 'need';
	height: number;		// % de la hauteur de la bande (need)
	color: string;		// fond du bloc (need)
	ink: boolean;			// true = texte sombre (pas clairs de la rampe)
	label: string;		// effectif affiché dans le bloc
	title: string;		// tooltip « 09:00–12:00 : 3 pers. minimum »
	row?: SliceRow;		// tranche derrière le bloc (cible du drag)
}

// Rampe séquentielle (bleu de l'app, clair → foncé) : la couleur d'un bloc suit l'effectif
// (1 → 6 et plus). Validée : luminosité strictement décroissante, pas adjacents séparables.
export const HR_COUNT_COLORS = ['#e4e9fb', '#b6c2f1', '#8498e4', '#5f73d3', '#4353b4', '#28337f'];

// État d'un glisser en cours sur l'histogramme d'une gestion. La fenêtre horaire et
// l'échelle d'effectif sont FIGÉES au pointerdown : la géométrie ne bouge pas sous le pointeur.
interface DragState {
	mode: 'create' | 'move' | 'start' | 'end' | 'count';
	group: CoverageGroup;
	rect: DOMRect;
	win: { start: number; end: number };
	scale: number;
	originMin: number;
	row: SliceRow;
	before: { start: string; end: string; min: number | null };
	moved: boolean;
}

// Effectifs minimum — /hr/coverage. Les jours gérés pareil sont REGROUPÉS en « gestions »
// (« Lun–Ven », « Sam »…) : une carte = des chips de jours + UN histogramme éditable à la
// souris (glisser = peindre une tranche à la hauteur de l'effectif voulu, bords = heures,
// bord haut = effectif, corps = déplacer), plus les inputs clavier. Enregistrer déploie
// chaque gestion sur ses jours et poste le DIFF. En dessous : les manques à venir sur les
// prochaines semaines en croisant plannings, exceptions et absences — clic = le planning.
@Component({
	selector: 'app-hr-coverage',
	standalone: true,
	imports: [MenuCommonComponent, FormsModule, ReactiveFormsModule, RouterLink, MatFormFieldModule, MatInputModule,
		MatSelectModule, MatButtonModule, MatIconModule, MatTooltipModule],
	templateUrl: './coverage.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './coverage.component.scss',
})
export class CoverageComponent implements OnInit, OnDestroy {

	public hrService = inject(HrService);
	private commonService = inject(CommonService);
	private loginService = inject(LoginService);
	private alertService = inject(AlertService);
	private router = inject(Router);
	private zone = inject(NgZone);

	public dayLabels = HR_DAY_LABELS;
	public dayLetters = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
	public weekdays = [1, 2, 3, 4, 5, 6, 7];
	public storeCtrl = new FormControl<number | null>(null);
	public activityCtrl = new FormControl<number | null>(null);
	public stores: Store[] = [];
	public activities: HrActivity[] = [];
	public storeActivities: HrActivity[] = [];

	public groups: CoverageGroup[] = [];
	public dirty = false;
	public saving = false;

	public shortfalls: ShortfallVM[] = [];
	public surpluses: SurplusVM[] = [];
	public horizonLabel = '';
	public isAdmin = false;
	public loaded = false;

	private schedules: HrSchedule[] = [];
	private overrides: HrOverride[] = [];
	private absences: HrAbsence[] = [];
	private requirements: HrRequirement[] = [];
	private from = '';
	private to = '';
	private drag: DragState | null = null;
	private onMove = (e: PointerEvent) => this.zone.run(() => this.dragMove(e));
	private onUp = () => this.zone.run(() => this.dragEnd());

	ngOnInit(): void {
		this.from = mondayOf(todayStr());
		this.to = addDays(this.from, HR_COVERAGE_WEEKS * 7 - 1);
		this.horizonLabel = HR_COVERAGE_WEEKS + ' semaines (du ' + this.fmt(this.from) + ' au ' + this.fmt(this.to) + ')';
		this.storeCtrl.valueChanges.subscribe(() => this.onStoreChange());
		this.activityCtrl.valueChanges.subscribe(() => this.buildGroups());
		this.load();
	}

	ngOnDestroy(): void {
		this.unbindDrag();
	}

	private fmt(dat: string): string {
		return dat.slice(8, 10) + '/' + dat.slice(5, 7);
	}

	private load(): void {
		forkJoin({
			requirements: this.hrService.getRequirements$(),
			schedules: this.hrService.getSchedules$(),
			activities: this.hrService.getActivities$(),
			overrides: this.hrService.getOverrides$(this.from, this.to),
			absences: this.hrService.getAbsences$(this.from, this.to, HR_ABSENCE_STATE_APPROVED),
			stores: this.commonService.getStores(),
			users: this.hrService.getUsers$(),
		}).subscribe(({ requirements, schedules, activities, overrides, absences, stores, users }) => {
			this.isAdmin = hrIsHrManager(this.loginService.loggeduser, users);		// la base fait foi (vieux JWT sans roles)
			this.requirements = requirements;
			this.schedules = schedules;
			this.overrides = overrides;
			this.absences = absences;
			this.stores = stores;
			this.activities = hrResolveActivities(activities, schedules, requirements);
			if (this.storeCtrl.value === null) {
				this.storeCtrl.setValue(this.loginService.currentstation?.store ?? stores[0]?.id ?? null, { emitEvent: false });
			}
			this.refreshStoreActivities();
			if (this.activityCtrl.value === null || !this.storeActivities.some(a => a.id === this.activityCtrl.value)) {
				this.activityCtrl.setValue(this.storeActivities[0]?.id ?? null, { emitEvent: false });
			}
			this.buildGroups();
			this.buildShortfalls();
			this.loaded = true;
		});
	}

	private onStoreChange(): void {
		this.refreshStoreActivities();
		this.activityCtrl.setValue(this.storeActivities[0]?.id ?? null, { emitEvent: false });
		this.buildGroups();
	}

	private refreshStoreActivities(): void {
		const store = this.storeCtrl.value;
		this.storeActivities = this.activities.filter(a => a.id_store === store && a.is_active !== false);
	}

	// ============================================================
	// Les gestions : des jours regroupés qui partagent les mêmes tranches
	// ============================================================

	// Reconstruit les gestions depuis les besoins : les jours qui ont EXACTEMENT les mêmes
	// tranches fusionnent dans une même carte. Les jours vides restent « sans contrainte ».
	private buildGroups(): void {
		const actId = this.activityCtrl.value;
		const bySig = new Map<string, CoverageGroup>();
		this.groups = [];
		for (const weekday of this.weekdays) {
			const rows = this.requirements
				.filter(r => hrRequirementActivityId(r) === actId && r.weekday === weekday)
				.sort((a, b) => (a.start_min ?? 0) - (b.start_min ?? 0))
				.map(r => ({ start: minutesLabel(r.start_min ?? 0), end: minutesLabel(r.end_min ?? 1440), min: r.min_count }));
			if (!rows.length) continue;
			const sig = rows.map(r => r.start + '>' + r.end + '=' + r.min).join('|');
			const found = bySig.get(sig);
			if (found) found.days.push(weekday);
			else {
				const group: CoverageGroup = { days: [weekday], rows };
				bySig.set(sig, group);
				this.groups.push(group);
			}
		}
		if (!this.groups.length) this.groups.push({ days: [1, 2, 3, 4, 5], rows: [] });		// amorce : une gestion Lun–Ven
		this.dirty = false;
	}

	// « Lun–Ven », « Sam », « Lun, Mar, Ven »… : libellé compact des jours d'une gestion.
	groupTitle(group: CoverageGroup): string {
		if (!group.days.length) return 'Aucun jour';
		const short = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
		const parts: string[] = [];
		for (let i = 0; i < group.days.length;) {
			let j = i;
			while (j + 1 < group.days.length && group.days[j + 1] === group.days[j] + 1) j++;
			parts.push(j >= i + 2 ? short[group.days[i] - 1] + '–' + short[group.days[j] - 1]
				: group.days.slice(i, j + 1).map(d => short[d - 1]).join(', '));
			i = j + 1;
		}
		return parts.join(', ');
	}

	inOtherGroup(group: CoverageGroup, weekday: number): boolean {
		return this.groups.some(g => g !== group && g.days.includes(weekday));
	}

	// Entrer/sortir un jour d'une gestion ; un jour pris à une autre gestion déménage.
	// Une gestion vidée (plus de jour, pas de tranche) disparaît d'elle-même.
	onToggleDay(group: CoverageGroup, weekday: number): void {
		if (!this.isAdmin) return;
		if (group.days.includes(weekday)) {
			group.days = group.days.filter(d => d !== weekday);
		} else {
			for (const g of this.groups) g.days = g.days.filter(d => d !== weekday);
			group.days = [...group.days, weekday].sort((a, b) => a - b);
		}
		this.groups = this.groups.filter(g => g === group || g.days.length > 0 || g.rows.length > 0);
		this.dirty = true;
	}

	onAddGroup(): void {
		this.groups.push({ days: [], rows: [] });
	}

	onRemoveGroup(group: CoverageGroup): void {
		const doIt = () => {
			this.groups = this.groups.filter(g => g !== group);
			this.dirty = this.dirty || group.rows.length > 0;
		};
		if (!group.rows.length || !group.days.length) { doIt(); return; }
		Swal.fire({
			title: 'Supprimer cette gestion ?',
			text: this.groupTitle(group) + ' : ses tranches d\'effectif seront retirées à l\'enregistrement.',
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler',
		}).then(r => { if (r.isConfirmed) doIt(); });
	}

	// Jours qui n'appartiennent à aucune gestion — donc sans contrainte d'effectif.
	get freeDaysLabel(): string {
		return this.weekdays
			.filter(d => !this.groups.some(g => g.days.includes(d)))
			.map(d => HR_DAY_LABELS[d - 1])
			.join(', ');
	}

	// ============================================================
	// L'histogramme d'une gestion : ouverture en fond, blocs à hauteur d'effectif
	// ============================================================

	get currentActivityObj(): HrActivity | null {
		return this.activities.find(a => a.id === this.activityCtrl.value) ?? null;
	}

	// Bornes numériques d'une tranche (fin « 24:00 » = minuit) ; null si la saisie est incomplète.
	private rowRange(row: SliceRow): { start: number; end: number } | null {
		const start = minutesOfHhmm(row.start);
		const end = row.end === '24:00' ? 1440 : minutesOfHhmm(row.end);
		return start === null || end === null ? null : { start, end };
	}

	// Fenêtre horaire commune des histogrammes : englobe horaires d'ouverture et tranches saisies,
	// arrondie à l'heure (défaut 07:00–20:00 quand rien n'est défini).
	timelineWindow(): { start: number; end: number } {
		let start = 7 * 60, end = 20 * 60;
		const activity = this.currentActivityObj;
		for (const d of this.weekdays) {
			for (const r of hrOpeningOf(activity, d)) {
				start = Math.min(start, r.start_min);
				end = Math.max(end, r.end_min);
			}
		}
		for (const group of this.groups) {
			for (const row of group.rows) {
				const range = this.rowRange(row);
				if (!range) continue;
				start = Math.min(start, range.start);
				end = Math.max(end, range.end);
			}
		}
		return { start: Math.floor(start / 60) * 60, end: Math.ceil(end / 60) * 60 };
	}

	// Libellés d'heures sous les histogrammes (toutes les 3 h).
	timelineTicks(): { left: number; label: string }[] {
		const w = this.timelineWindow();
		const out: { left: number; label: string }[] = [];
		for (let h = Math.ceil(w.start / 60); h * 60 <= w.end; h++) {
			if (h % 3 !== 0) continue;
			out.push({ left: ((h * 60 - w.start) / (w.end - w.start)) * 100, label: h + 'h' });
		}
		return out;
	}

	// Échelle d'effectif COMMUNE à toutes les cartes, avec une marge pour « tirer plus haut ».
	scaleMax(): number {
		let max = 0;
		for (const group of this.groups) {
			for (const row of group.rows) max = Math.max(max, row.min ?? 0);
		}
		return Math.max(4, max + 1);
	}

	// Lignes-guides horizontales (1, 2, 3… personnes) et leurs étiquettes à gauche.
	scaleGuides(): { bottom: number; label: string }[] {
		const scale = this.scaleMax();
		const out: { bottom: number; label: string }[] = [];
		for (let n = 1; n < scale; n++) out.push({ bottom: (n / scale) * 100, label: '' + n });
		return out;
	}

	groupTimeline(group: CoverageGroup): TimelineSeg[] {
		const w = this.timelineWindow();
		const span = Math.max(1, w.end - w.start);
		const scale = this.scaleMax();
		const out: TimelineSeg[] = [];
		for (const r of this.openUnion(group.days)) {
			out.push({
				left: ((r.start_min - w.start) / span) * 100,
				width: ((r.end_min - r.start_min) / span) * 100,
				cls: 'open', height: 100, color: '', ink: false, label: '',
				title: 'Ouvert ' + minutesLabel(r.start_min) + '–' + minutesLabel(r.end_min),
			});
		}
		for (const row of group.rows) {
			const range = this.rowRange(row);
			if (!range || range.end <= range.start) continue;
			const count = Math.max(0, row.min ?? 0);
			out.push({
				left: ((range.start - w.start) / span) * 100,
				width: ((range.end - range.start) / span) * 100,
				cls: 'need',
				height: count ? (count / scale) * 100 : 8,
				color: count ? HR_COUNT_COLORS[Math.min(count, HR_COUNT_COLORS.length) - 1] : '#cfd5e2',
				ink: count <= 3,
				label: '' + (row.min ?? ''),
				title: row.start + '–' + row.end + ' : ' + row.min + ' pers. minimum',
				row,
			});
		}
		return out;
	}

	// Union des horaires d'ouverture des jours de la gestion (fond de l'histogramme).
	private openUnion(days: number[]): { start_min: number; end_min: number }[] {
		const all = days
			.flatMap(d => hrOpeningOf(this.currentActivityObj, d))
			.sort((a, b) => a.start_min - b.start_min);
		const out: { start_min: number; end_min: number }[] = [];
		for (const r of all) {
			const last = out[out.length - 1];
			if (last && r.start_min <= last.end_min) last.end_min = Math.max(last.end_min, r.end_min);
			else out.push({ start_min: r.start_min, end_min: r.end_min });
		}
		return out;
	}

	// ============================================================
	// Histogramme : dessin, déplacement, redimensionnement, effectif (pointeur)
	// ============================================================

	private minuteAt(clientX: number, d: { rect: DOMRect; win: { start: number; end: number } }): number {
		const ratio = Math.max(0, Math.min(1, (clientX - d.rect.left) / Math.max(1, d.rect.width)));
		const raw = d.win.start + ratio * (d.win.end - d.win.start);
		return Math.max(d.win.start, Math.min(d.win.end, Math.round(raw / HR_GRID_SNAP_MIN) * HR_GRID_SNAP_MIN));
	}

	// L'effectif sous le pointeur : la hauteur dans la bande donne le nombre de personnes.
	private countAt(clientY: number, d: { rect: DOMRect; scale: number }): number {
		const ratio = (d.rect.bottom - clientY) / Math.max(1, d.rect.height);
		return Math.max(1, Math.min(d.scale, Math.round(ratio * d.scale)));
	}

	onLanePointerDown(e: PointerEvent, group: CoverageGroup, lane: HTMLElement): void {
		if (e.button !== 0 || !this.isAdmin) return;
		e.preventDefault();
		const rect = lane.getBoundingClientRect();
		const win = this.timelineWindow(), scale = this.scaleMax();
		const originMin = this.minuteAt(e.clientX, { rect, win });
		const row: SliceRow = {
			start: minutesLabel(originMin),
			end: minutesLabel(Math.min(win.end, originMin + HR_GRID_SNAP_MIN)),
			min: this.countAt(e.clientY, { rect, scale }),
		};
		this.drag = { mode: 'create', group, rect, win, scale, originMin, row, before: { ...row }, moved: false };
		this.bindDrag();
	}

	onSegPointerDown(e: PointerEvent, group: CoverageGroup, seg: TimelineSeg, lane: HTMLElement, mode: 'move' | 'start' | 'end' | 'count'): void {
		if (e.button !== 0 || !this.isAdmin || !seg.row) return;
		e.preventDefault();
		e.stopPropagation();
		const d: DragState = {
			mode, group, rect: lane.getBoundingClientRect(), win: this.timelineWindow(), scale: this.scaleMax(),
			originMin: 0, row: seg.row, before: { ...seg.row }, moved: false,
		};
		d.originMin = this.minuteAt(e.clientX, d);
		this.drag = d;
		this.bindDrag();
	}

	private bindDrag(): void {
		this.zone.runOutsideAngular(() => {
			window.addEventListener('pointermove', this.onMove);
			window.addEventListener('pointerup', this.onUp);
		});
	}

	private unbindDrag(): void {
		window.removeEventListener('pointermove', this.onMove);
		window.removeEventListener('pointerup', this.onUp);
	}

	private dragMove(e: PointerEvent): void {
		const d = this.drag;
		if (!d) return;
		const cur = this.minuteAt(e.clientX, d);
		const before = this.rowRange(d.before) ?? { start: d.win.start, end: d.win.start + HR_GRID_SNAP_MIN };
		const row = d.row;
		if (d.mode === 'create') {
			const s = Math.min(d.originMin, cur);
			row.start = minutesLabel(s);
			row.end = minutesLabel(Math.max(s + HR_GRID_SNAP_MIN, Math.max(d.originMin, cur)));
			row.min = this.countAt(e.clientY, d);
			if (cur !== d.originMin) d.moved = true;
			if (d.moved && !d.group.rows.includes(row)) d.group.rows.push(row);		// la tranche naît au premier mouvement
		} else if (d.mode === 'move') {
			const len = before.end - before.start;
			let s = before.start + (cur - d.originMin);
			s = Math.max(d.win.start, Math.min(d.win.end - len, s));
			row.start = minutesLabel(s);
			row.end = minutesLabel(s + len);
			if (cur !== d.originMin) d.moved = true;
		} else if (d.mode === 'start') {
			row.start = minutesLabel(Math.min(cur, before.end - HR_GRID_SNAP_MIN));
			if (cur !== d.originMin) d.moved = true;
		} else if (d.mode === 'end') {
			row.end = minutesLabel(Math.max(cur, before.start + HR_GRID_SNAP_MIN));
			if (cur !== d.originMin) d.moved = true;
		} else {		// count : l'effectif suit la hauteur du pointeur
			const count = this.countAt(e.clientY, d);
			if (count !== row.min) { row.min = count; d.moved = true; }
		}
		if (d.mode !== 'count') this.clampToNeighbours(d.group, row);
	}

	// Une tranche ne chevauche jamais une autre tranche de la même gestion : elle bute dessus.
	private clampToNeighbours(group: CoverageGroup, row: SliceRow): void {
		const range = this.rowRange(row);
		if (!range) return;
		let { start, end } = range;
		for (const other of group.rows) {
			if (other === row) continue;
			const o = this.rowRange(other);
			if (!o || o.end <= start || o.start >= end) continue;
			if (o.start >= (start + end) / 2) end = Math.min(end, o.start);
			else start = Math.max(start, o.end);
		}
		if (end - start < HR_GRID_SNAP_MIN) end = start + HR_GRID_SNAP_MIN;
		row.start = minutesLabel(start);
		row.end = minutesLabel(end);
	}

	private dragEnd(): void {
		const d = this.drag;
		this.drag = null;
		this.unbindDrag();
		if (!d || !d.moved) return;		// simple clic : la saisie précise se fait dans les inputs dessous
		d.group.rows.sort((a, b) => (minutesOfHhmm(a.start) ?? 0) - (minutesOfHhmm(b.start) ?? 0));
		this.dirty = true;
	}

	// ============================================================
	// Édition clavier des tranches
	// ============================================================

	onAddSlice(group: CoverageGroup): void {
		const last = group.rows[group.rows.length - 1];
		group.rows.push({ start: last?.end || '08:00', end: '', min: last?.min ?? 1 });
		this.dirty = true;
	}

	onRemoveSlice(group: CoverageGroup, idx: number): void {
		group.rows.splice(idx, 1);
		this.dirty = true;
	}

	onEdited(): void {
		this.dirty = true;
	}

	// Valide et DÉPLOIE les gestions en besoins par jour ; null si une saisie est invalide.
	private buildEdited(): HrRequirement[] | null {
		const actId = this.activityCtrl.value;
		if (actId === null) return null;
		const out: HrRequirement[] = [];
		for (const group of this.groups) {
			const label = this.groupTitle(group);
			const parsed: { start: number; end: number; min: number }[] = [];
			for (const row of group.rows) {
				const range = this.rowRange(row);
				const min = row.min != null ? +row.min : NaN;
				if (!range || isNaN(min) || min < 0) {
					this.alertService.error(label + ' : tranche incomplète (heures HH:MM et effectif requis).');
					return null;
				}
				if (range.end <= range.start) {
					this.alertService.error(label + ' : la fin doit être après le début.');
					return null;
				}
				parsed.push({ start: range.start, end: range.end, min });
			}
			parsed.sort((a, b) => a.start - b.start);
			for (let i = 1; i < parsed.length; i++) {
				if (parsed[i].start < parsed[i - 1].end) {
					this.alertService.error(label + ' : les tranches se chevauchent.');
					return null;
				}
			}
			for (const weekday of group.days) {
				for (const p of parsed) {
					out.push(Object.assign(new HrRequirement(), {
						id: 0, id_activity: actId > 0 ? actId : null, weekday,
						start_min: p.start, end_min: p.end, min_count: p.min,
					}));
				}
			}
		}
		return out;
	}

	onSave(): void {
		if (this.saving || !this.isAdmin) return;
		const actId = this.activityCtrl.value;
		const edited = this.buildEdited();
		if (edited === null || actId === null) return;

		// Ids réattribués PAR POSITION : pour chaque jour, les tranches triées d'avant et d'après
		// se correspondent une à une (UPDATE seulement si changée) ; le surplus s'insère ou s'efface.
		const posts = [];
		for (const weekday of this.weekdays) {
			const before = this.requirements
				.filter(r => hrRequirementActivityId(r) === actId && r.weekday === weekday)
				.sort((a, b) => (a.start_min ?? 0) - (b.start_min ?? 0));
			const after = edited
				.filter(r => r.weekday === weekday)
				.sort((a, b) => (a.start_min ?? 0) - (b.start_min ?? 0));
			const common = Math.min(before.length, after.length);
			for (let i = 0; i < common; i++) {
				after[i].id = before[i].id;
				if (before[i].start_min !== after[i].start_min || before[i].end_min !== after[i].end_min || before[i].min_count !== after[i].min_count) {
					posts.push(this.hrService.postHrEvent(EVENTTYP_UPDATE, OBJECTTYP_HR_REQUIREMENT, this.hrService.buildRequirementPayload(after[i])));
				}
			}
			for (let i = common; i < after.length; i++) {
				posts.push(this.hrService.postHrEvent(EVENTTYP_INSERT, OBJECTTYP_HR_REQUIREMENT, this.hrService.buildRequirementPayload(after[i])));
			}
			for (let i = common; i < before.length; i++) {
				posts.push(this.hrService.postHrEvent(EVENTTYP_DELETE, OBJECTTYP_HR_REQUIREMENT, this.hrService.buildRequirementPayload(before[i])));
			}
		}
		if (!posts.length) { this.dirty = false; return; }
		this.saving = true;
		forkJoin(posts).subscribe({
			next: () => { this.saving = false; this.alertService.success('Effectifs minimum enregistrés'); this.load(); },
			error: (e) => { this.saving = false; this.alertService.error('ERREUR Serveur : ' + (e?.error ?? '')); },
		});
	}

	// ============================================================
	// Écarts sur les prochaines semaines (tous services)
	// ============================================================
	//
	// DEUX écarts, pas un : le manque fait perdre du chiffre, le surnombre en coûte. Le second
	// n'est pas l'inverse anodin du premier — c'est la question que personne ne pose parce que
	// l'écran ne l'affichait pas, alors que le moteur savait y répondre depuis le début.

	// Situe une tranche en écart : semaine, jour, établissement, service, horaires.
	private gapOf(g: { dat: string; weekday: number; id_activity: number; start_min: number; end_min: number }): GapVM {
		const activity = this.activities.find(a => a.id === g.id_activity);
		return {
			week: isoWeekOf(g.dat),
			dayLabel: HR_DAY_LABELS[g.weekday - 1] + ' ' + this.fmt(g.dat),
			storeName: this.stores.find(st => st.id === activity?.id_store)?.name ?? '',
			activityName: activity?.name ?? ('Service #' + g.id_activity),
			hours: minutesLabel(g.start_min) + '–' + minutesLabel(g.end_min),
		};
	}

	private buildShortfalls(): void {
		const args = [this.from, this.to, this.schedules, this.overrides, this.absences, this.requirements] as const;
		this.shortfalls = computeCoverage(...args).map(s => ({ ...s, ...this.gapOf(s) }));
		this.surpluses = computeOverstaffing(...args).map(s => ({ ...s, ...this.gapOf(s) }));
	}

	onOpenPlanning(g: { dat: string }): void {
		this.router.navigate(['/hr/planning', g.dat]);
	}
}
